import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck, CheckCircle2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PageBackdrop } from "@/components/PageBackdrop";
import { Wordmark } from "@/components/Logo";
import { TimeWheel } from "@/components/TimeWheel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Website design & build",
  "AI tools for your operations",
  "SEO foundations",
  "Social media optimisation",
  "Lead generation & growth",
  "Ongoing care plan",
];

const SLOTS = Array.from({ length: 49 }, (_, i) => {
  const m = 8 * 60 + i * 15;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
});

const inputClass =
  "w-full rounded-xl border border-jade/20 bg-paper/60 px-4 py-3 text-sm text-ink placeholder:text-ink2/40 transition-colors duration-300";

const weekdayOptions = () => {
  const days = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (days.length < 21) {
    d.setDate(d.getDate() + 1);
    days.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  }
  return days;
};

const prettyDate = (iso) =>
  new Date(`${iso}T09:00:00`).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

const BookPage = () => {
  usePageMeta("Vagus Digital | Book a call");
  const dates = useMemo(weekdayOptions, []);
  const [form, setForm] = useState({
    name: "",
    email: "",
    service: SERVICES[0],
    date: dates[0],
    slot: "10:00",
    notes: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/bookings`, form, { timeout: 30000 });
      setStatus("booked");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please email groupvagus@gmail.com directly."
      );
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-paper pb-24 pt-32 lg:pt-40">
      <PageBackdrop src="/art/book.webp" />
      <div className="relative mx-auto max-w-3xl px-6 lg:px-10">
        <Link
          to="/"
          data-testid="book-home-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-jade transition-opacity duration-300 hover:opacity-70"
        >
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>

        {status === "booked" ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 rounded-2xl bg-white p-10 text-center shadow-ambient"
            data-testid="book-success"
          >
            <CheckCircle2 className="mx-auto h-10 w-10 text-jade" strokeWidth={1.75} />
            <h1 className="mt-5 font-display text-3xl font-light tracking-tight text-ink">
              You're in the diary.
            </h1>
            <p className="mx-auto mt-4 max-w-md leading-relaxed text-ink2">
              {form.name}, I've noted <strong>{prettyDate(form.date)}</strong> at{" "}
              <strong>{form.slot}</strong> UK time for{" "}
              <strong>{form.service.toLowerCase()}</strong>. You'll get a Google
              Meet invite to confirm, and if the time needs to move, just reply
              to that email.
            </p>
            <Link
              to="/"
              data-testid="book-success-home-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-jade px-7 py-3 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink"
            >
              Back to site
            </Link>
          </motion.div>
        ) : (
          <>
            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6 }}
              className="mt-10 text-xs font-medium uppercase tracking-[0.3em] text-jade"
            >
              Book a call
            </motion.p>
            <motion.h1
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.08 }}
              className="mt-4 font-display text-3xl font-light leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl"
              data-testid="book-heading"
            >
              Twenty minutes, <em className="italic text-jade">no obligation</em>.
            </motion.h1>
            <motion.p
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.14 }}
              className="mt-5 max-w-xl leading-relaxed text-ink2"
            >
              Pick a day and time that suits you and tell me what you're
              thinking. We'll meet on Google Meet and you'll leave knowing
              exactly where you stand.
            </motion.p>

            <motion.form
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.2 }}
              onSubmit={submit}
              className="mt-10 rounded-2xl bg-white p-8 shadow-ambient lg:p-10"
              data-testid="booking-form"
            >
              <div className="space-y-6">
                <div>
                  <label htmlFor="book-service" className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">
                    What's this about?
                  </label>
                  <select
                    id="book-service"
                    data-testid="book-service-select"
                    value={form.service}
                    onChange={update("service")}
                    className={`${inputClass} appearance-none`}
                  >
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="book-date" className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">
                    Pick a day
                  </label>
                  <select
                    id="book-date"
                    data-testid="book-date-select"
                    value={form.date}
                    onChange={update("date")}
                    className={`${inputClass} appearance-none`}
                  >
                    {dates.map((d) => (
                      <option key={d} value={d}>{prettyDate(d)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <span className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">
                    Time slot (UK time)
                  </span>
                  <TimeWheel options={SLOTS} value={form.slot} onChange={(slot) => setForm((f) => ({ ...f, slot }))} />
                  <p className="mt-2 text-center text-xs text-ink2/60" data-testid="book-slot-hint">Scroll or tap · 8am to 8pm, every day</p>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="book-name" className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">
                      Name
                    </label>
                    <input
                      id="book-name"
                      data-testid="book-name-input"
                      className={inputClass}
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      required
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label htmlFor="book-email" className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">
                      Email
                    </label>
                    <input
                      id="book-email"
                      type="email"
                      data-testid="book-email-input"
                      className={inputClass}
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      required
                      maxLength={200}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="book-notes" className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">
                    Anything I should know? (optional)
                  </label>
                  <textarea
                    id="book-notes"
                    data-testid="book-notes-input"
                    className={`${inputClass} min-h-[100px] resize-y`}
                    value={form.notes}
                    onChange={update("notes")}
                    placeholder="A line about your business and what you'd like to cover…"
                    maxLength={1000}
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-700" data-testid="book-error">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="book-submit-button"
                  disabled={status === "sending"}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-jade px-8 py-3.5 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <CalendarCheck className="h-4 w-4" />
                  {status === "sending" ? "Booking…" : "Request this slot"}
                </button>
                <p className="text-center text-xs text-ink2/50">
                  Requests are confirmed by email before anything is final.
                </p>
              </div>
            </motion.form>
          </>
        )}
      </div>
    </section>
  );
};

export default BookPage;